<script setup lang="ts">
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'
import { useIsMobile } from '@/composables/useIsMobile'
import { useRouter } from 'vue-router'
import BaseImage from '@/components/ui/BaseImage.vue'
import DescriptionRenderer from '@/components/descriptions/DescriptionRenderer.vue'
import CloseIcon from 'vue-material-design-icons/Close.vue'
import type { Character } from '@/types/domain'

const props = withDefaults(defineProps<{
  open?: boolean
  character?: Character | null
}>(), {
  open: false,
  character: null,
})

const emit = defineEmits<{
  (event: 'close'): void
}>()

const { isMobile } = useIsMobile()
const router = useRouter()

const close = (): void => emit('close')

const goToCharacter = async (): Promise<void> => {
  close()
  if (props.character?.id) {
    await router.push(`/character/${props.character.id}`)
  }
}
</script>

<template>
  <TransitionRoot :show="props.open" as="template">
    <Dialog class="character-drawer" @close="close">
      <!-- Backdrop -->
      <TransitionChild
        enter="character-drawer__backdrop-enter"
        enter-from="character-drawer__backdrop-enter-from"
        enter-to="character-drawer__backdrop-enter-to"
        leave="character-drawer__backdrop-leave"
        leave-from="character-drawer__backdrop-leave-from"
        leave-to="character-drawer__backdrop-leave-to"
      >
        <div class="character-drawer__backdrop" aria-hidden="true" @click="close" />
      </TransitionChild>

      <!-- Panel -->
      <TransitionChild
        as="template"
        :enter="isMobile ? 'character-drawer__panel-enter-mobile' : 'character-drawer__panel-enter-desktop'"
        :enter-from="isMobile ? 'character-drawer__panel-from-mobile' : 'character-drawer__panel-from-desktop'"
        :enter-to="isMobile ? 'character-drawer__panel-to-mobile' : 'character-drawer__panel-to-desktop'"
        :leave="isMobile ? 'character-drawer__panel-leave-mobile' : 'character-drawer__panel-leave-desktop'"
        :leave-from="isMobile ? 'character-drawer__panel-to-mobile' : 'character-drawer__panel-to-desktop'"
        :leave-to="isMobile ? 'character-drawer__panel-from-mobile' : 'character-drawer__panel-from-desktop'"
      >
        <DialogPanel
          class="character-drawer__panel"
          :class="{ 'character-drawer__panel--mobile': isMobile }"
        >
          <div class="character-drawer__header">
            <button class="character-drawer__close" type="button" :aria-label="`Close ${character?.character_name || 'character'} panel`" @click="close">
              <component :is="CloseIcon" :size="20" aria-hidden="true" />
            </button>
          </div>

          <div v-if="props.character" class="character-drawer__content">
            <BaseImage
              class="character-drawer__portrait"
              :src="props.character.imgpath"
              :alt="props.character.character_name"
              :image-focus="props.character.img_focus"
            />
            <h2 class="character-drawer__name">{{ props.character.character_name }}</h2>
            <DescriptionRenderer
              v-if="props.character.description"
              class="character-drawer__description"
              :markdown="props.character.description"
            />
            <button type="button" class="character-drawer__profile-link" @click="goToCharacter">
              View full profile →
            </button>
          </div>
        </DialogPanel>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped>
.character-drawer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  overflow: hidden;
}

/* Backdrop */
.character-drawer__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

/* Transitions — backdrop */
.character-drawer__backdrop-enter { transition: opacity 250ms ease; }
.character-drawer__backdrop-enter-from { opacity: 0; }
.character-drawer__backdrop-enter-to { opacity: 1; }
.character-drawer__backdrop-leave { transition: opacity 200ms ease; }
.character-drawer__backdrop-leave-from { opacity: 1; }
.character-drawer__backdrop-leave-to { opacity: 0; }

/* Panel — desktop: slides from right */
.character-drawer__panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(340px, 90vw);
  background: var(--theme-surface-solid);
  border-left: 1px solid var(--theme-border);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Mobile: bottom sheet */
.character-drawer__panel--mobile {
  top: auto;
  right: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  max-height: 80vh;
  border-left: none;
  border-top: 1px solid var(--theme-border);
  border-radius: 16px 16px 0 0;
}

/* Desktop panel transitions */
.character-drawer__panel-enter-desktop { transition: transform 250ms ease; }
.character-drawer__panel-from-desktop { transform: translateX(100%); }
.character-drawer__panel-to-desktop { transform: translateX(0); }
.character-drawer__panel-leave-desktop { transition: transform 200ms ease; }

/* Mobile panel transitions */
.character-drawer__panel-enter-mobile { transition: transform 250ms ease; }
.character-drawer__panel-from-mobile { transform: translateY(100%); }
.character-drawer__panel-to-mobile { transform: translateY(0); }
.character-drawer__panel-leave-mobile { transition: transform 200ms ease; }

/* Header */
.character-drawer__header {
  display: flex;
  justify-content: flex-end;
  padding: 12px 12px 0;
  flex-shrink: 0;
}

.character-drawer__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--theme-surface-interactive);
  border: none;
  border-radius: 8px;
  color: var(--theme-text-secondary);
  cursor: pointer;
  transition: background-color 150ms, color 150ms;
}

.character-drawer__close:hover {
  background: var(--theme-surface-interactive-hover);
  color: var(--theme-on-surface);
}

/* Content */
.character-drawer__content {
  padding: 12px 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.character-drawer__portrait {
  width: 100%;
  border-radius: 12px;
  object-fit: cover;
  max-height: 280px;
}

.character-drawer__name {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--theme-on-surface);
  margin: 0;
}

.character-drawer__description {
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--theme-text-secondary);
}

.character-drawer__description :deep(p) {
  margin: 0 0 0.75em;
}

.character-drawer__profile-link {
  display: inline-block;
  margin-top: 4px;
  background: transparent;
  border: none;
  color: var(--theme-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-align: left;
  transition: opacity 150ms;
}

.character-drawer__profile-link:hover {
  opacity: 0.8;
}
</style>
